// 3D (WebGL / Three.js) renderer entry. A self-contained VIEW over GameState +
// src/shared: it reads domain data and draws a 3D world, and only ever writes
// camera/view state (st.cam target + its own orbit). No game rules, no sim, no
// business logic. Selected by main.ts behind the ?r3d flag; the 2.5D renderer is
// untouched. Owner: client-render3d.

import {
  AmbientLight, Color, DirectionalLight, DoubleSide, Fog, HemisphereLight, Mesh,
  MeshLambertMaterial, PCFSoftShadowMap, PerspectiveCamera, Scene, Vector3, WebGLRenderer,
} from 'three'
import { DAY_TICKS } from '../../shared/constants'
import { hexToPixel } from '../../shared/hex'
import type { ClientMsg } from '../../shared/protocol'
import { HEX_SIZE, type GameState } from '../state'
import { dayGrade } from '../render/renderer'
import { buildChunkGeometry, tileHeight } from './terrainMesh'
import { EntityLayer3d, type UnitVis } from './entities3d'
import { Overlays3d } from './overlays3d'
import { Adventures3d } from './adventures3d'
import { Territory3d } from './territory3d'
import { Halo3d } from './halo3d'
import { Hud3d } from './hud3d'
import { buildGallery } from './gallery'
import { bindInput3d } from './input3d'
import { clampCamera, fogOf } from '../render/fog'
import { initMinimap } from '../render/minimap'
import { applyOrbit, createOrbit, easeOrbit, FOV } from './camera'

export function startRenderer3d(
  canvas: HTMLCanvasElement,
  st: GameState,
  send: (m: ClientMsg) => void,
): void {
  const renderer = new WebGLRenderer({ canvas, antialias: true })
  renderer.setClearColor(0x0c1622, 1)
  // Mobile perf: fill rate is the bottleneck on phones — cap the pixel ratio lower
  // and halve the shadow map on coarse-pointer (touch) devices.
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = PCFSoftShadowMap
  renderer.localClippingEnabled = true // construction sites rise via clip planes
  const scene = new Scene()
  const camera = new PerspectiveCamera(FOV, 1, 1, 40000)
  // Distance fog matched to the sky each frame — far terrain melts into the horizon
  // instead of ending in a hard silhouette line. Range tracks the zoom (see frame()).
  const airFog = new Fog(0x0c1622, 1200, 4000)
  scene.fog = airFog

  // Lighting: cool sky + warm ground bounce, a key sun that casts soft shadows, a
  // little fill. Sun colour/intensity and the sky track the time of day.
  const hemi = new HemisphereLight(0xbcd4ff, 0x4a4636, 0.85)
  scene.add(hemi)
  const sun = new DirectionalLight(0xfff2d8, 1.05)
  sun.position.set(-0.5, 1.1, 0.35)
  sun.castShadow = true
  sun.shadow.mapSize.set(coarse ? 1024 : 2048, coarse ? 1024 : 2048)
  sun.shadow.bias = -0.0004
  sun.shadow.normalBias = 2
  sun.shadow.camera.near = 10
  sun.shadow.camera.far = 3200
  scene.add(sun)
  scene.add(sun.target)
  const ambient = new AmbientLight(0xffffff, 0.22)
  scene.add(ambient)
  // Sky: deep navy night → plum dusk → pale warm parchment day (the island floats
  // in a bright airy void by day, like the tabletop look we're chasing).
  const nightSky = new Color(0x0c1622), duskSky = new Color(0x3a2c34), daySky = new Color(0xece4d2)
  const sky = new Color()

  function updateDayNight(): void {
    const g = dayGrade((st.tick % DAY_TICKS) / DAY_TICKS)
    const day = 1 - g.night
    sun.color.setRGB(g.r / 255, g.g / 255, g.b / 255)
    sun.intensity = 0.30 + day * 1.35
    hemi.intensity = 0.42 + day * 0.62
    ambient.intensity = 0.14 + day * 0.20
    if (day < 0.5) sky.copy(nightSky).lerp(duskSky, day * 2)
    else sky.copy(duskSky).lerp(daySky, (day - 0.5) * 2)
    renderer.setClearColor(sky, 1)
    airFog.color.copy(sky)
  }

  // Keep thin terrain details visible from either side. The geometry itself
  // still needs outward normals so shadow bias moves away from each surface.
  const mat = new MeshLambertMaterial({ vertexColors: true, flatShading: true, side: DoubleSide })
  const orbit = createOrbit()
  // The opening starts close enough to see the people and the first plots.
  st.on('openingFocus', () => { orbit.dist = 700 })
  // Dev handle: tweak camera live (window.ORBIT3D.dist / .pitch / .yawTarget).
  ;(window as unknown as Record<string, unknown>).ORBIT3D = orbit
  const meshes = new Map<string, { version: number; mesh: Mesh }>()
  // Per-unit final world position, shared so selection rings and HP bars track the
  // exact point each unit's mesh occupies (entities writes it each frame).
  const unitAnim = new Map<number, UnitVis>()
  const entities = new EntityLayer3d(scene, unitAnim)
  const overlays = new Overlays3d(scene, unitAnim)
  const adventures = new Adventures3d(scene)
  const territory = new Territory3d(scene)
  const halo = new Halo3d(scene)

  // Dev/testing handle: project a world point to screen px (for headless click tests).
  ;(window as unknown as Record<string, unknown>).R3D = {
    orbit, entities,
    screenOf(x: number, y: number, z: number): { x: number; y: number } {
      const v = new Vector3(x, y, z).project(camera)
      return { x: (v.x * 0.5 + 0.5) * window.innerWidth, y: (-v.y * 0.5 + 0.5) * window.innerHeight }
    },
    hexTop(q: number, r: number): { x: number; y: number; z: number } {
      const p = hexToPixel(q, r, HEX_SIZE)
      return { x: p.x, y: tileHeight(st, q, r), z: p.y }
    },
    dbg: { renderer, scene, sun, fog: () => fogOf(st) }, // headless diagnostics
  }

  // Dev model gallery: skip the game world, show one of every model in a grid.
  const gallery = new URLSearchParams(location.search).has('gallery')
  const galleryAt = gallery ? buildGallery(scene, mat, orbit) : null
  if (gallery) {
    st.tick = DAY_TICKS / 2
    ;(window as unknown as Record<string, unknown>).GALLERY3D = galleryAt
  }
  const hud = gallery ? null : new Hud3d(camera, st, unitAnim)
  if (!gallery) {
    // "The Realm" minimap: self-driving 2D module (reads GameState, world coords);
    // we only supply the 3D view footprint for its viewport rectangle.
    initMinimap(st, () => {
      const k = (2 * orbit.dist * Math.tan((FOV * Math.PI) / 180 / 2)) / window.innerHeight
      return { w: window.innerWidth * k, h: window.innerHeight * k }
    })
  }

  function resize(): void {
    const w = window.innerWidth, h = window.innerHeight
    renderer.setPixelRatio(Math.min(coarse ? 1.5 : 2, window.devicePixelRatio || 1))
    renderer.setSize(w, h, false)
    camera.aspect = w / h
    if (gallery) camera.setViewOffset(w, h, w > 900 ? -w * 0.13 : 0, w > 900 ? 0 : h * 0.13, w, h)
    camera.updateProjectionMatrix()
  }
  window.addEventListener('resize', resize)
  resize()

  // Camera + raycast picking + commands live in the input controller (view-only:
  // reads GameState, calls send). The gallery is view-only, so skip its bindings.
  if (!gallery) bindInput3d({ canvas, st, send, camera, scene, orbit })

  // ---- render loop -----------------------------------------------------------
  let lastFogVersion = -1
  let lastFogChunks = new Set<string>()
  let lastT = performance.now()
  function frame(): void {
    const now = performance.now()
    const dt = Math.min(0.05, (now - lastT) / 1000)
    lastT = now
    // Q/E held = free continuous spin (lands wherever released); Shift+Q/E snaps 60°
    // via yawTarget, eased here when not free-spinning.
    if (orbit.spinVel !== 0) { orbit.yaw += orbit.spinVel * dt; orbit.yawTarget = orbit.yaw }
    else easeOrbit(orbit)
    updateDayNight()
    if (galleryAt) {
      st.cam.x = galleryAt.x; st.cam.y = galleryAt.z
      sun.position.set(Math.sin(orbit.yaw - .8) * 250, 350, Math.cos(orbit.yaw - .8) * 250)
      sun.target.position.set(0, 0, 0)
      sun.shadow.camera.left = sun.shadow.camera.bottom = -140
      sun.shadow.camera.right = sun.shadow.camera.top = 140
      sun.shadow.camera.updateProjectionMatrix()
      airFog.near = 1500; airFog.far = 4000
      applyOrbit(camera, orbit, st)
      renderer.render(scene, camera)
      requestAnimationFrame(frame)
      return
    }
    clampCamera(st) // keep the target inside the discovered map
    // Fog: when vision changes, only the currently/previously visible chunks need
    // re-dimming — interior explored (all-dim) and void chunks are unaffected.
    const fog = fogOf(st)
    let fogDirty: Set<string> | null = null
    if (fog && fog.version !== lastFogVersion) {
      fogDirty = new Set([...fog.chunks, ...lastFogChunks, ...fog.memDirty])
      lastFogChunks = new Set(fog.chunks)
      lastFogVersion = fog.version
    }
    // sync chunk meshes with loaded/updated chunks (+ fog re-bakes)
    for (const [key, ck] of st.chunks) {
      const cur = meshes.get(key)
      if (cur && cur.version === ck.version && !fogDirty?.has(key)) continue
      if (cur) { scene.remove(cur.mesh); cur.mesh.geometry.dispose() }
      const mesh = new Mesh(buildChunkGeometry(st, ck.cx, ck.cy, fog), mat)
      mesh.userData.terrain = true // raycast target for ground picking
      mesh.castShadow = true
      mesh.receiveShadow = true
      scene.add(mesh)
      meshes.set(key, { version: ck.version, mesh })
    }
    for (const [key, m] of meshes) {
      if (st.chunks.has(key)) continue
      scene.remove(m.mesh); m.mesh.geometry.dispose(); meshes.delete(key)
    }
    entities.sync(st, dt)
    territory.sync(st)
    halo.sync(st)
    overlays.sync(st)
    adventures.sync(st)
    applyOrbit(camera, orbit, st)
    // Sun + its shadow box ride the camera target so shadows stay crisp wherever you
    // look; fog range scales with zoom so the horizon always melts at the same depth.
    sun.position.set(st.cam.x - 560, 760, st.cam.y + 390) // lower sun → readable shadows
    sun.target.position.set(st.cam.x, 0, st.cam.y)
    const ext = Math.min(1500, Math.max(320, orbit.dist * 0.95))
    const sc = sun.shadow.camera
    if (Math.abs(sc.right - ext) > 1) {
      sc.left = -ext; sc.right = ext; sc.top = ext; sc.bottom = -ext
      sc.updateProjectionMatrix()
    }
    airFog.near = orbit.dist * 1.2
    airFog.far = orbit.dist * 3.6
    renderer.render(scene, camera)
    hud?.draw(st)
    requestAnimationFrame(frame)
  }
  requestAnimationFrame(frame)
}
